import { describeAutofillSuggestion, type FormField } from "@aam/shared";
import { useAutofill } from "../context/AutofillContext";
import { useFormStore } from "../store/formStore";
import { FieldHelp } from "./ui/Input";

/**
 * Provenance for one field: where the value came from, whether it needs a
 * second look, and — for the PII the plan deliberately left blank — an explicit
 * offer to copy the stored value forward.
 *
 * Renders nothing outside the portal, where there is no autofill provider.
 */
export function AutofillFieldNote({ field }: { field: FormField }) {
  const autofill = useAutofill();
  const value = useFormStore((state) => state.formData[field.id]);
  if (!autofill?.plan) return null;

  const suggestion = autofill.suggestionFor(field.id);
  if (!suggestion) return null;

  const revealing = autofill.revealing === field.id;
  const revealError =
    autofill.revealError?.fieldId === field.id ? autofill.revealError.message : null;

  if (suggestion.sensitive) {
    const filled = typeof value === "string" && value.trim().length > 0;
    if (filled) {
      return (
        <FieldHelp className="text-status-active-text">
          Copied from your {suggestion.sourceLabel}. Check it before you submit.
        </FieldHelp>
      );
    }
    const preview = autofill.plan.sensitivePreviews.find(
      (entry) => entry.fieldId === field.id
    );
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void autofill.reveal(field.id)}
          disabled={revealing}
          className="focus-ring inline-flex min-h-10 items-center gap-2 rounded-xl border-2 border-accent bg-surface-raised px-3 py-1.5 text-xs font-bold text-accent transition-colors hover:bg-accent-muted disabled:opacity-60"
        >
          {revealing ? "Loading…" : `Use saved value${preview ? ` (${preview.masked})` : ""}`}
        </button>
        <span className="text-xs text-content-muted">
          Stored from your {suggestion.sourceLabel}
        </span>
        {revealError && (
          <span className="text-xs text-status-rejected-text" role="alert">
            {revealError}
          </span>
        )}
      </div>
    );
  }

  if (!autofill.isAutofilled(field.id)) return null;

  const needsReview = suggestion.needsReview;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <span
        className={
          needsReview
            ? "inline-flex items-center gap-1.5 rounded-lg border border-status-pending-border bg-status-pending-bg px-2 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-status-pending-text"
            : "inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-surface-overlay px-2 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-content-muted"
        }
      >
        {needsReview ? "Confirm" : "Autofilled"}
      </span>
      <span className="text-xs text-content-secondary">
        {describeAutofillSuggestion(suggestion)}
      </span>
      <button
        type="button"
        onClick={() => autofill.clearField(field.id)}
        className="focus-ring rounded-lg px-2 py-1 text-xs font-semibold text-content-muted underline-offset-2 hover:text-content-primary hover:underline"
      >
        Clear
      </button>
    </div>
  );
}
